import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useCan, Can } from '../../lib/rbac';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  Tag, Plus, Trash2, ChevronDown, ChevronUp, Loader2, Calendar,
  Package, X
} from 'lucide-react';

const SITE_FLAGS: Record<string, string> = { MLA: '🇦🇷', MLM: '🇲🇽', MLC: '🇨🇱', MCO: '🇨🇴', MLU: '🇺🇾', MLB: '🇧🇷' };

interface Campaign {
  id: string;
  type: string;
  name: string;
  sub_type: string;
  status: string;
  start_date: string;
  finish_date: string;
  site_id: string;
  marketplace_user_id: number;
}

interface CampaignItem {
  id: string;
  status: string;
  price: number;
  original_price: number;
  currency_id: string;
  start_date: string;
  end_date: string;
  max_discounted_price?: number;
  min_discounted_price?: number;
  suggested_discounted_price?: number;
  net_proceeds?: { amount: number; currency: string } | Record<string, { amount: number; currency: string }>;
}

const STATUS_MAP: Record<string, 'ok' | 'warning' | 'error' | 'info'> = {
  started: 'ok', pending: 'warning', finished: 'info', candidate: 'warning',
};

export default function PromotionsTab({ sellerId }: { sellerId: number }) {
  const { t } = useTranslation();
  const canCreate = useCan()('ml.promotions');
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [campaignItems, setCampaignItems] = useState<Record<string, CampaignItem[]>>({});
  const [itemsLoading, setItemsLoading] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [childUsers, setChildUsers] = useState<Array<{ user_id: number; site_id: string }>>([]);

  // Create form state
  const [newName, setNewName] = useState('');
  const [newSite, setNewSite] = useState('');
  const [newStart, setNewStart] = useState('');
  const [newEnd, setNewEnd] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    const res = await api.get<Campaign[]>(`/mercadolibre/promotions?seller_id=${sellerId}`);
    if (res.success && res.data) {
      setCampaigns(res.data);
    }
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  // Load child users for create form
  useEffect(() => {
    api.get<unknown>(`/mercadolibre/metrics-live/caps?seller_id=${sellerId}`).then((res: any) => {
      if (res.success && res.data) {
        setChildUsers(res.data.map((c: any) => ({ user_id: c.user_id, site_id: c.site_id })));
      }
    });
  }, [sellerId]);

  const toggleExpand = async (campaign: Campaign) => {
    if (expanded === campaign.id) {
      setExpanded(null);
      return;
    }
    setExpanded(campaign.id);
    if (!campaignItems[campaign.id]) {
      setItemsLoading(campaign.id);
      const res = await api.get<any>(
        `/mercadolibre/promotions/${campaign.id}/items?seller_id=${sellerId}&user_id=${campaign.marketplace_user_id}`
      );
      if (res.success && res.data) {
        setCampaignItems(prev => ({ ...prev, [campaign.id]: res.data.results || [] }));
      }
      setItemsLoading('');
    }
  };

  const handleCreate = async () => {
    if (!newName || !newSite || !newStart || !newEnd) return;
    const child = childUsers.find(c => c.site_id === newSite);
    if (!child) { alert('Site not found'); return; }

    setCreateLoading(true);
    const res = await api.post(`/mercadolibre/promotions?seller_id=${sellerId}`, {
      user_id: child.user_id,
      name: newName,
      start_date: `${newStart}T00:00:00`,
      finish_date: `${newEnd}T23:59:59`,
    });
    setCreateLoading(false);

    if (res.success) {
      setShowCreate(false);
      setNewName(''); setNewSite(''); setNewStart(''); setNewEnd('');
      loadCampaigns();
    } else {
      alert(`Error: ${(res as any).error}`);
    }
  };

  const handleDelete = async (campaign: Campaign) => {
    if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;
    const res = await api.delete(
      `/mercadolibre/promotions/${campaign.id}?seller_id=${sellerId}&user_id=${campaign.marketplace_user_id}`
    );
    if (res.success) {
      loadCampaigns();
    } else {
      alert(`Error: ${(res as any).error}`);
    }
  };

  const activeCampaigns = campaigns.filter(c => c.status === 'started');
  const otherCampaigns = campaigns.filter(c => c.status !== 'started');

  if (loading) return <div className="p-8 text-center text-[var(--color-text-muted)]">{t('common.loading')}</div>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Tag className="w-5 h-5 text-[var(--color-primary)]" />
          <h3 className="text-sm font-medium text-[var(--color-text)]">
            {t('ml.promo.title')} ({campaigns.length})
          </h3>
        </div>
        <Can cap="ml.promotions">
          <button
            onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:opacity-90"
          >
            <Plus className="w-3.5 h-3.5" /> {t('ml.promo.create')}
          </button>
        </Can>
      </div>

      {/* Create form */}
      {showCreate && canCreate && (
        <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4 space-y-3">
          <h4 className="text-sm font-medium text-[var(--color-text)]">{t('ml.promo.newCampaign')}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input
              type="text"
              placeholder={t('ml.promo.campaignName')}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
            />
            <select
              value={newSite}
              onChange={(e) => setNewSite(e.target.value)}
              className="px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
            >
              <option value="">{t('ml.promo.selectSite')}</option>
              {childUsers.map(c => (
                <option key={c.site_id} value={c.site_id}>{SITE_FLAGS[c.site_id] || ''} {c.site_id}</option>
              ))}
            </select>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[var(--color-text-muted)]" />
              <input
                type="date"
                value={newStart}
                onChange={(e) => setNewStart(e.target.value)}
                className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[var(--color-text-muted)]" />
              <input
                type="date"
                value={newEnd}
                onChange={(e) => setNewEnd(e.target.value)}
                className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setShowCreate(false)} className="px-3 py-1.5 text-sm text-[var(--color-text-muted)] hover:bg-[var(--color-bg)]">
              {t('common.cancel')}
            </button>
            <button
              onClick={handleCreate}
              disabled={createLoading || !newName || !newSite || !newStart || !newEnd}
              className="flex items-center gap-1 px-4 py-1.5 bg-[var(--color-primary)] text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
            >
              {createLoading && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {t('ml.promo.create')}
            </button>
          </div>
        </div>
      )}

      {/* No campaigns */}
      {campaigns.length === 0 && (
        <div className="p-8 text-center text-[var(--color-text-muted)]">
          {t('ml.promo.noCampaigns')}
        </div>
      )}

      {/* Active campaigns */}
      {activeCampaigns.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-green-400 uppercase tracking-wide">{t('ml.promo.active')}</h4>
          {activeCampaigns.map(c => (
            <CampaignCard
              key={c.id}
              campaign={c}
              expanded={expanded === c.id}
              items={campaignItems[c.id]}
              itemsLoading={itemsLoading === c.id}
              onToggle={() => toggleExpand(c)}
              onDelete={() => handleDelete(c)}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Other campaigns */}
      {otherCampaigns.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide">
            {t('ml.promo.pendingFinished')}
          </h4>
          {otherCampaigns.map(c => (
            <CampaignCard
              key={c.id}
              campaign={c}
              expanded={expanded === c.id}
              items={campaignItems[c.id]}
              itemsLoading={itemsLoading === c.id}
              onToggle={() => toggleExpand(c)}
              onDelete={() => handleDelete(c)}
              t={t}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CampaignCard({
  campaign: c, expanded, items, itemsLoading, onToggle, onDelete, t
}: {
  campaign: Campaign;
  expanded: boolean;
  items?: CampaignItem[];
  itemsLoading: boolean;
  onToggle: () => void;
  onDelete: () => void;
  t: (key: string) => string;
}) {
  const can = useCan();
  const canMoney = can('global.view_money');
  const startDate = new Date(c.start_date).toLocaleDateString();
  const endDate = new Date(c.finish_date).toLocaleDateString();

  return (
    <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)]">
      <div
        className="flex items-center gap-3 p-3 cursor-pointer hover:bg-[var(--color-bg)] transition-colors"
        onClick={onToggle}
      >
        <span className="text-lg">{SITE_FLAGS[c.site_id] || '🌍'}</span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[var(--color-text)]">{c.name}</span>
            <StatusBadge status={STATUS_MAP[c.status] || 'unknown'} label={c.status} size="sm" />
          </div>
          <div className="text-xs text-[var(--color-text-muted)]">
            {c.id} · {c.site_id} · {startDate} → {endDate}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {c.status !== 'finished' && can('ml.promotions') && (
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="p-1 rounded hover:bg-red-500/20 text-[var(--color-text-muted)] hover:text-red-400"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-[var(--color-text-muted)]" /> : <ChevronDown className="w-4 h-4 text-[var(--color-text-muted)]" />}
        </div>
      </div>

      {/* Expanded: campaign items */}
      {expanded && (
        <div className="border-t border-[var(--color-border)] p-3">
          {itemsLoading ? (
            <div className="flex items-center gap-2 text-sm text-[var(--color-text-muted)]">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading')}
            </div>
          ) : !items || items.length === 0 ? (
            <div className="text-sm text-[var(--color-text-muted)]">{t('ml.promo.noItems')}</div>
          ) : (
            <div className="space-y-2">
              <div className="text-xs text-[var(--color-text-muted)]">
                {items.length} {t('ml.promo.itemsInCampaign')}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left p-2 text-[var(--color-text-muted)]">Item</th>
                      <th className="text-center p-2 text-[var(--color-text-muted)]">{t('common.status')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('ml.promo.originalPrice')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('ml.promo.dealPrice')}</th>
                      <th className="text-right p-2 text-[var(--color-text-muted)]">{t('ml.promo.discount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => {
                      const discount = item.original_price > 0 && item.price > 0
                        ? Math.round((1 - item.price / item.original_price) * 100)
                        : 0;
                      return (
                        <tr key={item.id} className="border-b border-[var(--color-border)]">
                          <td className="p-2 text-[var(--color-text)] font-mono text-xs">{item.id}</td>
                          <td className="p-2 text-center">
                            <StatusBadge status={STATUS_MAP[item.status] || 'unknown'} label={item.status} size="sm" />
                          </td>
                          <td className="p-2 text-right text-[var(--color-text-muted)]">
                            {canMoney ? `$${item.original_price?.toFixed(2)} ${item.currency_id}` : '\u2014'}
                          </td>
                          <td className="p-2 text-right text-[var(--color-text)] font-medium">
                            {!canMoney ? '\u2014' : item.price > 0 ? `$${item.price.toFixed(2)}` : (
                              item.suggested_discounted_price ? (
                                <span className="text-blue-400">${item.suggested_discounted_price.toFixed(2)} <span className="text-xs">(sug)</span></span>
                              ) : '-'
                            )}
                          </td>
                          <td className="p-2 text-right">
                            {discount > 0 ? (
                              <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-500/20 text-green-400">
                                -{discount}%
                              </span>
                            ) : '-'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
